import { FunctionComponent, Ref, useId, useState } from 'react';
import styled from 'styled-components';
import {
  Badge,
  BadgeVariant,
  boxShadow,
  Button,
  Carousel,
  Checkbox,
  paletteHelper,
  radiusHelper,
  spacingHelper,
  Text,
  typographyHelper,
} from '@property.com.au/ui-kit';
import { PropertyFeature } from '../../../../../../app-kit/components/PropertyFeatures/subcomponents/PropertyFeature';
import { PropertyFeatures } from '../../../../../../app-kit/components/PropertyFeatures/PropertyFeatures';
import { PropertyTileImage } from '../../../../../../app-kit/components/PropertyTile/PropertyTile';

const TILE_WIDTH = `220px`;

const PropertyCarouselSelectorTileContainer = styled.article`
  width: ${TILE_WIDTH};
  border-radius: ${radiusHelper('large')};
  ${boxShadow.defaultBoxShadow}

  img {
    width: ${TILE_WIDTH};
    height: 134px;
    object-fit: cover;
    border-radius: ${radiusHelper('large')} ${radiusHelper('large')} 0 0;
  }
`;

const StyledPcaImage = styled.div`
  position: relative;
  display: block;

  &:hover {
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: ${radiusHelper('large')} ${radiusHelper('large')} 0 0;
      transition: background-color 0.3s ease-in;
      background-color: ${paletteHelper('backgroundHoveredPrimary')};
      opacity: 0.1;
      pointer-events: none;
    }
  }
`;

const StyledCheckbox = styled(Checkbox)`
  .input:checked:not(:disabled, :hover) ~ .checkbox {
    background-color: ${paletteHelper('backgroundHoveredBrandStatic')};
    border-color: ${paletteHelper('backgroundHoveredBrandStatic')};
  }

  .label {
    ${typographyHelper('subtitle02')}
    color: ${paletteHelper('textPrimary')};
  }

  margin-top: ${spacingHelper('extraSmall')};
  align-items: center;
`;

const TileCopyContainer = styled.div`
  padding: ${spacingHelper('0 small small')};
  /* negative margin to adjust spacing since badge is translated */
  margin-top: -3px;
`;

const TileAddress = styled(Text)`
  color: ${paletteHelper('textSecondary')};
  margin-top: ${spacingHelper('extraSmall')};
  margin-bottom: ${spacingHelper('twoExtraSmall')};

  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const TileTitle = styled(Text)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const StyledBadge = styled(Badge)`
  transform: translate(${spacingHelper('extraSmall')}, -50%);
`;

export interface PropertyCarouselSelectorTileProps {
  property: {
    propertyId: string;
    image: {
      alt: string;
      jpgUri: string;
      webpUri: string;
    } | null;
    badge: {
      text: string;
      variant: BadgeVariant;
    };
    title?: string;
    subtitle?: string;
    address: string;
    features: PropertyFeature[] | null;
  };
  onSelect: (address: string) => void;
  isSelected: boolean;
}
const PropertyCarouselSelectorTile: FunctionComponent<PropertyCarouselSelectorTileProps> = ({
  property: { propertyId, image, badge, title, subtitle, address, features },
  isSelected,
  onSelect,
}) => {
  const id = useId();
  return (
    <PropertyCarouselSelectorTileContainer>
      <StyledPcaImage>
        <PropertyTileImage image={image} />
      </StyledPcaImage>
      <StyledBadge variant={badge.variant}>{badge.text}</StyledBadge>
      <TileCopyContainer>
        <TileTitle variant="body02Bold">{title}</TileTitle>
        <Text variant="caption">{subtitle}</Text>
        <TileAddress title={address} variant="caption">
          {address}
        </TileAddress>
        {features && <PropertyFeatures items={features} />}
        <StyledCheckbox
          id={`${id}-${propertyId}-property-tile-checkbox`}
          label="Include"
          checked={isSelected}
          onChange={() => {
            onSelect(propertyId);
          }}
          dangerouslySetClassNames={{
            input: 'input',
            checkbox: 'checkbox',
            label: 'label',
          }}
          // fixes caching issue with back and forward browser navigation
          autoComplete="off"
        />
      </TileCopyContainer>
    </PropertyCarouselSelectorTileContainer>
  );
};

export interface PropertyCarouselSelectorProps {
  properties: PropertyCarouselSelectorTileProps['property'][];
  actions: string[];
  appendChat: (text: string, visibility: 'visible' | 'hidden') => Promise<void>;
  userInteractionRef?: Ref<HTMLDivElement>;
}

const ActionButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${spacingHelper('medium medium 0 medium')};
  gap: ${spacingHelper('extraSmall')};
`;

const StyledCarousel = styled(Carousel)`
  .carouselList {
    padding-bottom: ${spacingHelper('twoExtraSmall')};
  }
`;

export const PropertyCarouselSelector: FunctionComponent<PropertyCarouselSelectorProps> = ({
  properties,
  appendChat,
  actions,
  userInteractionRef,
}) => {
  const [selectedProperty, setSelectedProperty] = useState<string[]>([]);
  const propertyCarouselId = useId();
  const handleSelect = (propertyId: string) => {
    setSelectedProperty((prevSelected) => {
      if (prevSelected.includes(propertyId)) {
        return prevSelected.filter((id) => id !== propertyId);
      }
      return [...prevSelected, propertyId];
    });
  };
  const handleCompare = (action: string) => {
    const selectedProperties = properties.filter((property) =>
      selectedProperty.includes(property.propertyId),
    );
    const text = `${action}: ${selectedProperties.map((property) => property.address).join('; ')}`;
    appendChat(text, 'hidden').catch((error) => {
      console.error('Error appending chat:', error);
    });
  };

  return (
    <div>
      <StyledCarousel
        dangerouslySetClassNames={{
          leftScrollButton: 'leftScrollButton',
          rightScrollButton: 'rightScrollButton',
          carouselList: 'carouselList',
        }}
        insideGap="extraSmall"
      >
        {properties.map((property) => (
          <PropertyCarouselSelectorTile
            key={property.propertyId + propertyCarouselId}
            property={property}
            onSelect={handleSelect}
            isSelected={selectedProperty.includes(property.propertyId) || false}
          />
        ))}
      </StyledCarousel>

      <ActionButtonWrapper ref={userInteractionRef}>
        {actions.map((action) => (
          <Button
            key={action + propertyCarouselId}
            variant="outline-secondary"
            sizeVariant="medium"
            onClick={() => handleCompare(action)}
            disabled={selectedProperty.length < 1}
          >
            {action}
          </Button>
        ))}
      </ActionButtonWrapper>
    </div>
  );
};
